import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getTodaysAnalytics } from '../../../services/analyticsService';
import { getMonthlyVisits } from '../../../services/analyticsService';

const MONTH_NAMES = {
  '01': 'Oca', '02': 'Şub', '03': 'Mar', '04': 'Nis',
  '05': 'May', '06': 'Haz', '07': 'Tem', '08': 'Ağu',
  '09': 'Eyl', '10': 'Eki', '11': 'Kas', '12': 'Ara'
};

const formatMonthLabel = (monthKey) => {
  const [year, month] = monthKey?.split('-');
  return `${MONTH_NAMES?.[month] || month} ${year}`;
};

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [monthlyVisits, setMonthlyVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [analyticsResult, monthlyResult] = await Promise.all([
        getTodaysAnalytics(),
        getMonthlyVisits()
      ]);

      if (analyticsResult?.error) {
        console.error('Error loading analytics:', analyticsResult?.error);
      } else if (analyticsResult?.data) {
        setAnalytics(analyticsResult?.data);
      }

      if (!monthlyResult?.error) {
        setMonthlyVisits(monthlyResult?.data || []);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card text-center py-12">
        <Icon name="Loader" size={48} color="var(--color-primary)" className="mx-auto mb-4 animate-spin" />
        <p className="text-muted-foreground">Analitik verileri yükleniyor...</p>
      </div>
    );
  }

  const stats = [
    {
      id: 1,
      label: 'Bugünkü Ziyaretler',
      value: analytics?.totalVisits?.toString() || '0',
      change: '+12%',
      changeType: 'positive',
      icon: 'Users',
      color: 'var(--color-primary)'
    },
    {
      id: 2,
      label: 'Buton Tıklamaları',
      value: analytics?.dailyInteractions?.toString() || '0',
      change: '+8%',
      changeType: 'positive',
      icon: 'MousePointerClick',
      color: 'var(--color-success)'
    },
    {
      id: 3,
      label: 'Bu Ay QR Tarama',
      value: (() => {
        const currentMonth = new Date()?.toISOString()?.substring(0, 7);
        const current = monthlyVisits?.find(m => m?.month === currentMonth);
        return (current?.count || 0)?.toString();
      })(),
      change: 'Bu ay',
      changeType: 'neutral',
      icon: 'TrendingUp',
      color: 'var(--color-accent)'
    },
    {
      id: 4,
      label: 'Aktif Çekiliş',
      value: '2',
      change: 'Devam ediyor',
      changeType: 'neutral',
      icon: 'Gift',
      color: 'var(--color-destructive)'
    }
  ];

  const contentTypeStats = analytics?.contentTypeStats ?
    Object.entries(analytics?.contentTypeStats)?.map(([type, count]) => ({
      type: type,
      count: count,
      color: 'var(--color-primary)'
    })) : [];

  const contentTypeLabels = {
    dailyMessage: 'Günlük Mesaj',
    miniTask: 'Mini Görev',
    poll: 'Anket',
    giveaway: 'Çekiliş',
    surprise: 'Sürpriz Gün',
    humor: 'Mizah',
    socialAwareness: 'Sosyal Farkındalık',
  };

  const maxMonthlyCount = Math.max(...(monthlyVisits?.map(m => m?.count) || [1]), 1);

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
          <Icon name="BarChart3" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            Analitik Gösterge Paneli
          </h3>
          <p className="caption text-muted-foreground">
            Kampanya performans metrikleri
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {stats?.map((stat) => (
          <div key={stat?.id} className="card">
            <div className="flex items-start justify-between mb-4">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${stat?.color}15` }}
              >
                <Icon name={stat?.icon} size={24} color={stat?.color} />
              </div>
              <span
                className={`caption font-medium ${
                  stat?.changeType === 'positive' ? 'text-success'
                    : stat?.changeType === 'negative' ? 'text-destructive' : 'text-muted-foreground'
                }`}
              >
                {stat?.change}
              </span>
            </div>
            <h4 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
              {stat?.value}
            </h4>
            <p className="caption text-muted-foreground">
              {stat?.label}
            </p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        <div className="card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="PieChart" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h4 className="text-base md:text-lg font-heading font-semibold text-foreground">
                İçerik Tipi Dağılımı
              </h4>
              <p className="caption text-muted-foreground">
                Son 30 gün
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {contentTypeStats?.length > 0 ? (
              contentTypeStats?.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-foreground font-medium">
                      {contentTypeLabels?.[item?.type] || item?.type}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {item?.count}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min((item?.count / Math.max(...contentTypeStats?.map(s => s?.count))) * 100, 100)}%`,
                        backgroundColor: item?.color
                      }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Icon name="BarChart3" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <p className="text-muted-foreground">Henüz veri yok</p>
              </div>
            )}
          </div>
        </div>

        {/* Monthly QR Scan Breakdown */}
        <div className="card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Icon name="QrCode" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h4 className="text-base md:text-lg font-heading font-semibold text-foreground">
                Aylık QR Tarama Sayısı
              </h4>
              <p className="caption text-muted-foreground">
                Araç ziyaretleri aya göre
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {monthlyVisits?.length > 0 ? (
              monthlyVisits?.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-foreground font-medium">
                      {formatMonthLabel(item?.month)}
                    </span>
                    <span className="text-sm font-semibold text-accent">
                      {item?.count?.toLocaleString('tr-TR')} araç
                    </span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min((item?.count / maxMonthlyCount) * 100, 100)}%`,
                        backgroundColor: 'var(--color-accent)'
                      }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Icon name="QrCode" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <p className="text-muted-foreground">Henüz ziyaret verisi yok</p>
              </div>
            )}
          </div>

          {monthlyVisits?.length > 0 && (
            <div className="pt-4 border-t border-border mt-4">
              <p className="caption text-muted-foreground text-center">
                Toplam {monthlyVisits?.reduce((sum, m) => sum + (m?.count || 0), 0)?.toLocaleString('tr-TR')} araç ziyareti
              </p>
            </div>
          )}
        </div>
      </div>
      {/* Poll Results */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="BarChart3" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h4 className="text-base md:text-lg font-heading font-semibold text-foreground">
              Son Anket Sonuçları
            </h4>
            <p className="caption text-muted-foreground">
              {analytics?.pollResults?.[0]?.date}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-foreground font-medium mb-4">
            {analytics?.pollResults?.[0]?.question}
          </p>

          {analytics?.pollResults?.[0]?.options?.map((option, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-foreground">
                  {option?.label}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {option?.votes} oy
                  </span>
                  <span className="text-sm font-medium text-primary">
                    %{option?.percentage}
                  </span>
                </div>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${option?.percentage}%` }}
                />
              </div>
            </div>
          ))}

          <div className="pt-4 border-t border-border">
            <p className="caption text-muted-foreground text-center">
              Toplam {analytics?.pollResults?.[0]?.totalVotes} oy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;